import { useState, useEffect } from 'react';
import { Stack, Container, Group, Divider, ScrollArea } from '@mantine/core';
import { useSchemaForm } from '@ayocore/platform/client/hooks/useSchemaForm';
import leadSchemaDefinition from '../../../../schemas/lead.schema';
import {
  useLeadRepository,
  useLeadStatusRepository,
  useLeadTypeRepository,
  useLeadSourceRepository,
  useSalesAreaRepository,
} from '@ayocore/erp/crm/client/rpc';
import type { CreateLead } from '../../../../schemas/lead.schema';
import logger from '@ayocore/platform/common/helpers/logger';
import { useAuthController } from '@ayocore/platform/identity/client/useAuthController';
import { useUserRepository } from '@ayocore/platform/identity/client/rpc';
import { PartnerSelect } from '@ayocore/erp/crm/client/components/Partners/PartnerSelect';

/**
 * CreateLeadForm - Lead creation form with clean pattern.
 *
 * @param defaultLeadTypeSlug Slug типа лида, который будет подставлен по умолчанию.
 *   Также используется для поиска первого статуса воронки. По умолчанию — ACQUISITION
 *   (привлечение нового клиента).
 *   Используйте этот параметр, чтобы открывать форму создания лида заранее заданного типа
 *   (например, RETENTION для удержания или WINBACK для реактивации, или кастомных типов
 *   из клиентских модулей).
 */
export function CreateLeadForm({
  onLeadCreated,
  initialData,
  emailId,
  defaultLeadTypeSlug = 'ACQUISITION',
  partner,
  manager,
}: {
  onLeadCreated?: (leadId?: number) => void;
  initialData?: Partial<CreateLead>;
  emailId?: number;
  defaultLeadTypeSlug?: string;
  /** Если передан — поле партнёра предзаполняется и блокируется */
  partner?: { id: number; name: string };
  /** Если передан — ответственным назначается менеджер партнёра, а не текущий пользователь */
  manager?: { id: number; name: string };
}) {
  // Initialize repository proxies
  const leadRepository = useLeadRepository();
  const leadStatusRepository = useLeadStatusRepository();
  const leadTypeRepository = useLeadTypeRepository();
  const leadSourceRepository = useLeadSourceRepository();
  const salesAreaRepository = useSalesAreaRepository();
  const userRepository = useUserRepository();
  const { user } = useAuthController();
  const [defaultLeadTypeId, setDefaultLeadTypeId] = useState<number | undefined>(undefined);

  const SchemaForm = useSchemaForm<CreateLead>(leadSchemaDefinition.createSchema, {
    initialValues: { lead_type_id: defaultLeadTypeId },
    hideDescriptions: true,
  });

  // Выбираем тип лида по умолчанию, потом подтягиваем первый статус его воронки.
  //
  // Правила подбора:
  //  1. Если ровно один активный тип — берём его (типичный случай grasias с единой
  //     воронкой INCOMING_REQUEST). Игнорируем defaultLeadTypeSlug, чтобы не пытаться
  //     подставить неактивный «системный» дефолт.
  //  2. Иначе среди активных ищем тот, что совпадает с defaultLeadTypeSlug.
  //  3. Иначе — не подставляем ничего, пользователь выбирает вручную.
  useEffect(() => {
    const loadDefaults = async () => {
      try {
        const activeTypes = await leadTypeRepository.findActive();

        let leadType: { id: number; slug?: string | null } | null = null;
        if (activeTypes.length === 1) {
          leadType = activeTypes[0];
        } else if (activeTypes.length > 1) {
          leadType = activeTypes.find(t => t.slug === defaultLeadTypeSlug) ?? null;
          if (!leadType) {
            logger.warn(
              `CreateLeadForm: active lead type "${defaultLeadTypeSlug}" not found ` +
                `(active: ${activeTypes.map(t => t.slug).join(', ')})`
            );
          }
        } else {
          logger.warn('CreateLeadForm: нет ни одного активного типа лида');
        }

        if (!leadType) return;

        setDefaultLeadTypeId(leadType.id);
        SchemaForm.setFieldValue('lead_type_id', leadType.id);

        const statuses = await leadStatusRepository
          .where('lead_type_id', leadType.id)
          .orderBy('sort_order', 'asc')
          .limit(1)
          .fetch();
        if (statuses.length > 0) {
          SchemaForm.setFieldValue('lead_status_id', statuses[0].id);
        }
      } catch (error) {
        logger.error('Failed to load lead defaults:', error);
      }
    };
    loadDefaults();
  }, [defaultLeadTypeSlug]);

  // Подставляем текущего пользователя как менеджера (если не передан manager из пропсов)
  useEffect(() => {
    if (user?.id && !manager) {
      SchemaForm.setFieldValue('manager_id', user.id);
    }
  }, [user?.id, manager]);

  // Если передан manager из пропсов — используем его
  useEffect(() => {
    if (manager?.id) {
      SchemaForm.setFieldValue('manager_id', manager.id);
    }
  }, [manager?.id]);

  // Set initial data if provided
  useEffect(() => {
    if (initialData) {
      Object.entries(initialData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          SchemaForm.setFieldValue(key as keyof CreateLead, value);
        }
      });
    }
  }, [initialData]);

  const handleSubmit = async (values: CreateLead) => {
    // Create lead using repository
    let newLead;
    if (emailId) {
      // Use special method for email-based leads with file attachments
      newLead = await leadRepository.createLeadByEmail(emailId, values);
    } else {
      // Use regular create method
      newLead = await leadRepository.create(values);
    }

    // Show success message
    SchemaForm.setSuccess(`Лид "${newLead.title}" успешно создан!`);

    // Call the callback to refresh list and close modal after delay
    setTimeout(() => {
      if (onLeadCreated) {
        onLeadCreated(newLead.id);
      }
    }, 1500);

    return { success: true };
  };

  const handleCancel = () => {
    SchemaForm.reset();
    SchemaForm.clearMessages();
    if (onLeadCreated) {
      onLeadCreated();
    }
  };

  return (
    <form
      onSubmit={SchemaForm.handleSubmit(handleSubmit)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Scrollable Form Content */}
      <ScrollArea.Autosize flex={1} mih={0} scrollbarSize={0}>
        <Container size='lg' px={{ base: 'xs', sm: 'sm' }} py='xs' maw='100%'>
          <Stack gap='xs'>
            <SchemaForm.Messages />
            {/* lead_type_id set programmatically; field removed to avoid extra handlers */}

            {/* Название — на всю ширину */}
            <SchemaForm.Field.title />

            {/* Менеджер и партнёр */}
            <Group grow align='flex-start'>
              <SchemaForm.Field.manager_id
                repo={userRepository}
                displayField='name'
                placeholder='Выберите менеджера...'
              />
              <PartnerSelect
                value={SchemaForm.values.partner_id?.toString() || ''}
                onChange={value =>
                  SchemaForm.setFieldValue('partner_id', value ? parseInt(value) : null)
                }
                initialOption={
                  partner
                    ? { value: partner.id.toString(), label: partner.name }
                    : undefined
                }
                disabled={!!partner}
              />
            </Group>

            <SchemaForm.Field.description />

            <Divider />

            {/* Атрибутика лида: канал привлечения и территория */}
            <Group grow>
              <SchemaForm.Field.lead_source_id
                repo={leadSourceRepository.filterActive().orderBy('sort_order', 'asc')}
                displayField='name'
                placeholder='Выберите источник...'
              />
              <SchemaForm.Field.sales_area_id
                repo={salesAreaRepository}
                displayField='name'
                placeholder='Выберите регион...'
              />
            </Group>

            <Divider />

            {/* Financial Details */}
            <Group grow>
              <SchemaForm.Field.amount />
              <SchemaForm.Field.expected_close_date />
            </Group>

            <Divider />

            {/* Additional Information */}
            <SchemaForm.Field.notes />
          </Stack>
        </Container>
      </ScrollArea.Autosize>

      {/* Fixed Footer with Action Buttons */}
      <div
        style={{
          borderTop: '1px solid #e9ecef',
          backgroundColor: 'transparent',
          padding: '8px 0 0',
          flexShrink: 0,
        }}
      >
        <Group justify='flex-end'>
          <SchemaForm.buttons.cancel onClick={handleCancel} label='Отмена' />

          <SchemaForm.buttons.submit label='Создать лид' size='xs' />
        </Group>
      </div>
    </form>
  );
}

export default CreateLeadForm;
